/** 解析原子图片组件 attrs 中的图片地址 */
export function getAtomImageUrl(attrs: object): string {
  const getFirstValidItem = (arr: unknown[]) => {
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.find((item) => item !== null && item !== undefined);
    }
    return null;
  };

  let url = _get(attrs, 'url.value');
  const textValueArr = _get(attrs, 'textValue.value', []);
  const textValue = getFirstValidItem(textValueArr);
  if (textValue) {
    const dataURLInfo = _get(textValue, 'dataURLInfo');
    const dataURLPrefix = _get(textValue, 'dataURLPrefix');
    url = (dataURLPrefix && dataURLInfo)
      ? `${dataURLPrefix},${dataURLInfo}`
      : _get(textValue, 'url', url);
  }
  return resolveAbsoluteImageUrl(url || '');
}

/** 将相对路径转为可用于 img/Viewer 的绝对地址 */
export function resolveAbsoluteImageUrl(url: string): string {
  if (!url) {
    return '';
  }
  if (/^(https?:\/\/|data:|blob:|\/\/)/i.test(url)) {
    return url;
  }
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

/** 判断 URL 是否与当前页面同源 */
export const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

/** 从图片 URL 推断下载文件名 */
export function resolveImageFilenameFromUrl(url: string, fallbackPrefix = 'image'): string {
  if (url.startsWith('data:')) {
    const match = url.match(/^data:image\/(\w+)/);
    const ext = match ? (match[1] === 'jpeg' ? 'jpg' : match[1]) : 'png';
    return `${fallbackPrefix}-${Date.now()}.${ext}`;
  }
  try {
    const pathname = new URL(url, window.location.href).pathname;
    const name = decodeURIComponent(pathname.split('/').pop() || '');
    if (name && /\.\w+$/.test(name)) {
      return name;
    }
  } catch {
    // ignore invalid url
  }
  return `${fallbackPrefix}-${Date.now()}.png`;
}

/**
 * 触发浏览器文件下载。
 * 对 blob: 链接延迟 revoke，避免下载尚未开始就释放资源。
 */
export function triggerFileDownload(href: string, filename: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.style.display = 'none';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    if (href.startsWith('blob:')) {
      URL.revokeObjectURL(href);
    }
  }, 1000);
}

const fetchBlobViaXHR = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.withCredentials = true;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) {
        resolve(xhr.response);
        return;
      }
      reject(new Error(`xhr status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('xhr network error'));
    xhr.send();
  });
};

const loadImageBlobViaCanvas = (url: string, sameOrigin: boolean): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!sameOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('canvas toBlob failed'));
        }
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
};

/** 拉取远程/同源图片为 Blob，避免 <a download> 直接打开图片页 */
const fetchImageBlob = async (url: string): Promise<Blob> => {
  const sameOrigin = isSameOriginUrl(url);

  try {
    const response = await fetch(url, {
      credentials: 'include',
      mode: 'cors',
    });
    if (response.ok) {
      return await response.blob();
    }
  } catch {
    // try next strategy
  }

  try {
    return await fetchBlobViaXHR(url);
  } catch {
    // try next strategy
  }

  return loadImageBlobViaCanvas(url, sameOrigin);
};

/** 下载 URL 对应的原图（支持 data/blob/http，统一走 Blob 触发下载） */
export async function downloadImageFromUrl(
  url: string,
  filename?: string,
): Promise<boolean> {
  const resolvedUrl = resolveAbsoluteImageUrl(url);
  if (!resolvedUrl) {
    return false;
  }

  const downloadName = filename || resolveImageFilenameFromUrl(resolvedUrl);

  try {
    if (resolvedUrl.startsWith('data:') || resolvedUrl.startsWith('blob:')) {
      triggerFileDownload(resolvedUrl, downloadName);
      return true;
    }

    const blob = await fetchImageBlob(resolvedUrl);
    const blobUrl = URL.createObjectURL(blob);
    triggerFileDownload(blobUrl, downloadName);
    return true;
  } catch (error) {
    console.error('下载图片失败:', error);
    return false;
  }
};
